// The Sanskrit practice games: one question maker per game, all driven by a seeded random function, so the same seed
// always makes the same exercise (the practice sets in bank.ts keep only seeds). Each maker takes the level and the
// chosen topics (TopicScope, see topics.ts) and draws from the whole course: every root, lakāra and pada, every noun of
// the vocabulary in every case and number, sentences built from subject, object and verb, sandhi joins made by the
// sandhi engine and the course's sandhi, compound and story examples. A question's `key` is its fingerprint: two
// questions with the same key are the same exercise.

import type {
    Bi,
    Lakara,
    Pada,
    Purusha,
    SkillId,
    Vacana,
    Vibhakti
} from '@/sanskrit/lib/sanskrit/types'
import type { ChoiceOption } from '@/sanskrit/lib/content/lesson-types'
import type { VocabEntry } from '@/sanskrit/lib/sanskrit/vocabulary'
import type { SamasaType } from '@/sanskrit/lib/sanskrit/samasa'
import type { SandhiExample, VowelRule } from '@/sanskrit/lib/sanskrit/sandhi'

import { CORE_DHATUS, getDhatu, padasOf } from '@/sanskrit/lib/sanskrit/dhatus'
import { getParadigm } from '@/sanskrit/lib/sanskrit/paradigms'
import { declineStem, getNounParadigm } from '@/sanskrit/lib/sanskrit/nouns'
import { VOCABULARY, getVocab } from '@/sanskrit/lib/sanskrit/vocabulary'
import { SAMASA_EXAMPLES, SAMASA_TYPES } from '@/sanskrit/lib/sanskrit/samasa'
import { STORIES } from '@/sanskrit/lib/content/stories'
import { aksharas } from '@/sanskrit/lib/sanskrit/script'
import { pick, seededShuffle } from '@/sanskrit/lib/random'
import { verbGlossEn, whoOf } from '@/sanskrit/lib/sanskrit/describe'
import { PURUSHAS, VACANAS } from '@/sanskrit/lib/sanskrit/types'

import {
    ALL_SANDHI_EXAMPLES,
    VOWEL_EXAMPLES,
    VOWEL_RULES,
    combineVowels
} from '@/sanskrit/lib/sanskrit/sandhi'

import {
    LAKARA_LABEL,
    PURUSHA_LABEL,
    VACANA_LABEL,
    VIBHAKTI_LABEL
} from '@/sanskrit/lib/sanskrit/labels'

export type PracticeQuestion =
    | { kind: 'mcq'; key: string; q: Bi; sa?: string; options: ChoiceOption[]; answer: number; explain: Bi; skill: SkillId; item?: string; playAudio?: boolean }
    | { kind: 'listen'; key: string; q: Bi; sa: string; options: string[]; answer: string; skill: SkillId; item?: string }
    | { kind: 'build'; key: string; prompt: Bi; tiles: string[]; answer: string[]; orderFree?: boolean; skill: SkillId; item?: string; hint?: Bi }
    | { kind: 'arrange'; key: string; prompt: Bi; pieces: string[]; answer: string; skill: SkillId; item?: string }
    | { kind: 'reorder'; key: string; prompt: Bi; items: string[]; skill: SkillId }

type Rand = () => number

/** A sandhi group of the topic filter: one vowel rule, or visarga / consonant sandhi. */
export type SandhiKind = VowelRule | 'visarga' | 'vyanjana'

/** What the chosen topics allow (see topics.ts). A missing list means "the level's usual range". */
export interface TopicScope {
    lakaras?: Lakara[]
    vibhaktis?: Vibhakti[]
    vacanas?: Vacana[]
    purushas?: Purusha[]
    sandhi?: SandhiKind[]
    samasa?: SamasaType[]
}

const ALL_VIBS: Vibhakti[] = [1, 2, 3, 4, 5, 6, 7, 8]
const CASE_VIBS: Vibhakti[] = [1, 2, 3, 4, 5, 6, 7]

const PRONOUN: Record<Purusha, Record<Vacana, string>> = {
    uttama: { eka: 'अहम्', dvi: 'आवाम्', bahu: 'वयम्' },
    madhyama: { eka: 'त्वम्', dvi: 'युवाम्', bahu: 'यूयम्' },
    prathama: { eka: 'सः', dvi: 'तौ', bahu: 'ते' }
}

const FEMININE_PRONOUN: Record<Vacana, string> = { eka: 'सा', dvi: 'ते', bahu: 'ताः' }

const seedOf = (rand: Rand): number => Math.floor(rand() * 1e9)

const firstSense = (text: string): string => text.split(/[,;(]/)[0].trim()

/** A word written before another word: a final म् becomes anusvāra, as in the course's own sentences. */
const medial = (word: string): string => (word.endsWith('म्') ? `${word.slice(0, -2)}ं` : word)

/** Build a multiple-choice question with the correct option inserted at a random position. */
const mcq = (rand: Rand, base: Omit<Extract<PracticeQuestion, { kind: 'mcq' }>, 'kind' | 'options' | 'answer'>, correct: ChoiceOption, wrong: ChoiceOption[]): PracticeQuestion => {
    const distinct: ChoiceOption[] = []
    const seen = new Set<string>([JSON.stringify(correct)])

    for (const w of wrong) {
        const k = JSON.stringify(w)
        if (seen.has(k)) continue
        seen.add(k)
        distinct.push(w)
        if (distinct.length === 3) break
    }

    const options = seededShuffle([correct, ...distinct], seedOf(rand))
    return { kind: 'mcq', ...base, options, answer: options.findIndex(o => JSON.stringify(o) === JSON.stringify(correct)) }
}

// ── Verbs ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface VerbScope {
    dhatuIds?: string[]
    lakaras?: Lakara[]
    level?: number
    vacanas?: Vacana[]
    purushas?: Purusha[]
}

const levelLakaras = (level: number): Lakara[] => (level >= 3 ? ['lat', 'lrt', 'lan', 'lot', 'vidhilin'] : level === 2 ? ['lat', 'lrt', 'lan'] : ['lat'])

const VERIFIED_ROOTS = () => CORE_DHATUS.filter(d => d.status === 'verified')

const pickCell = (rand: Rand, scope: VerbScope) => {
    const roots = scope.dhatuIds?.map(id => getDhatu(id)).filter(Boolean) ?? VERIFIED_ROOTS()
    const level = scope.level ?? 1
    const lakaras: Lakara[] = scope.lakaras ?? levelLakaras(level)

    for (let attempt = 0; attempt < 30; attempt++) {
        const d = pick(roots, rand)
        if (!d) continue
        const pada: Pada = pick(padasOf(d), rand)
        const lakara = pick(lakaras, rand)
        const paradigm = getParadigm(d.id, lakara, pada)
        if (!paradigm || paradigm.status !== 'verified') continue
        const p = pick(scope.purushas ?? PURUSHAS, rand)
        const v = scope.vacanas ? pick(scope.vacanas, rand) : level === 1 && rand() < 0.4 ? 'eka' : pick(VACANAS, rand)
        if (!paradigm.forms[p]?.[v]?.[0]) continue
        return { d, pada, lakara, paradigm, p, v }
    }

    const d = getDhatu('path')!
    return { d, pada: 'P' as Pada, lakara: 'lat' as Lakara, paradigm: getParadigm('path', 'lat', 'P')!, p: 'uttama' as Purusha, v: 'eka' as Vacana }
}

/** Nouns that can stand as the subject of a sentence, with the classes the decliner covers. */
const SUBJECT_NOUNS = ['balakah', 'balika', 'chatrah', 'chatra', 'siksakah', 'sevakah', 'putrah', 'mitram', 'vanarah', 'gajah', 'asvah', 'kakah', 'mayurah', 'khagah', 'mrgah']

const declinable = (v: VocabEntry | undefined): v is VocabEntry => Boolean(v && v.stem && v.cls && ['a-m', 'a-n', 'aa-f', 'i-m', 'u-m', 'ii-f'].includes(v.cls))

/** A subject for a third-person verb: a pronoun or a noun in the nominative of that number. */
const thirdPersonSubject = (rand: Rand, v: Vacana): { sa: string, noun?: VocabEntry } => {
    const r = rand()

    if (r < 0.25) return { sa: PRONOUN.prathama[v] }
    // ते is also the feminine dual, so सा and ताः only: a subject must leave one right answer.
    if (r < 0.4 && v !== 'dvi') return { sa: FEMININE_PRONOUN[v] }

    const nouns = SUBJECT_NOUNS.map(getVocab).filter(declinable)
    const noun = pick(nouns, rand)

    return noun ? { sa: declineStem(noun.stem as string, noun.cls!)[1][v][0], noun } : { sa: PRONOUN.prathama[v] }
}

/** धातु चुनौती: choose the form that completes "subject ___". */
export const verbProductionQuestion = (rand: Rand, scope: VerbScope = {}): PracticeQuestion => {
    const { d, pada, lakara, paradigm, p, v } = pickCell(rand, scope)
    const correct = paradigm.forms[p][v][0]
    const subject = p === 'prathama' ? thirdPersonSubject(rand, v) : { sa: PRONOUN[p][v] }
    // ते (they) also reads as a feminine or neuter dual, so the third-person dual is never offered as a wrong answer.
    const alsoRight = subject.sa === 'ते' ? paradigm.forms.prathama.dvi[0] : null
    const others = PURUSHAS.flatMap(pp => VACANAS.map(vv => paradigm.forms[pp][vv][0])).filter(f => f && f !== correct && f !== alsoRight)
    const who = subject.noun
        ? {
            hi: `${subject.sa} एक संज्ञा है, इसलिए प्रथम पुरुष, ${VACANA_LABEL[v].hi}।`,
            en: `${subject.sa} is a noun, so prathama puruṣa, ${VACANA_LABEL[v].en.toLowerCase()}.`
        }
        : {
            hi: `${subject.sa} = ${PURUSHA_LABEL[p].short} पुरुष, ${VACANA_LABEL[v].hi}।`,
            en: `${subject.sa} is ${PURUSHA_LABEL[p].short} puruṣa, ${VACANA_LABEL[v].en.toLowerCase()}.`
        }

    return mcq(rand, {
        key: `vp:${d.id}:${lakara}:${pada}:${p}:${v}:${subject.sa}`,
        q: {
            hi: `${d.root} (${d.meaning.hi}), ${LAKARA_LABEL[lakara].sa}: "${subject.sa} ___" के लिए सही रूप?`,
            en: `${d.root} (${d.meaning.en}), ${LAKARA_LABEL[lakara].sa}: which form completes "${subject.sa} ___"?`
        },
        sa: `${subject.sa} ___`,
        explain: {
            hi: `${who.hi} इसलिए ${correct}।`,
            en: `${who.en} So the form is ${correct} (${verbGlossEn({ dhatuId: d.id, lakara, purusha: p, vacana: v })}).`
        },
        skill: 'verb-production',
        item: `verb:${d.id}:${lakara}:${pada}:${p}:${v}`,
        playAudio: false
    }, correct, seededShuffle(others, seedOf(rand)))
}

/** Identify person and number of a given form. */
export const verbIdentifyQuestion = (rand: Rand, scope: VerbScope = {}): PracticeQuestion => {
    const { d, pada, lakara, paradigm, p, v } = pickCell(rand, scope)
    const form = paradigm.forms[p][v][0]
    const label = (pp: Purusha, vv: Vacana): Bi => ({ hi: `${PURUSHA_LABEL[pp].short} पुरुष, ${VACANA_LABEL[vv].hi}`, en: `${PURUSHA_LABEL[pp].short} puruṣa, ${VACANA_LABEL[vv].en.toLowerCase()}` })
    const wrong = PURUSHAS.flatMap(pp => VACANAS.map(vv => ({ pp, vv }))).filter(x => !(x.pp === p && x.vv === v) && paradigm.forms[x.pp][x.vv][0] !== form)

    return mcq(rand, {
        key: `vi:${d.id}:${lakara}:${pada}:${p}:${v}`,
        q: { hi: `यह रूप (${LAKARA_LABEL[lakara].sa}) किस पुरुष और वचन में है?`, en: `Which person and number is this form (${LAKARA_LABEL[lakara].sa})?` },
        sa: form,
        explain: { hi: `${form}: ${whoOf(p, v).hi}।`, en: `${form}: ${whoOf(p, v).en}.` },
        skill: 'conjugation',
        item: `verb:${d.id}:${lakara}:${pada}:${p}:${v}`
    }, label(p, v), seededShuffle(wrong, seedOf(rand)).map(x => label(x.pp, x.vv)))
}

const LAKARA_CLUE: Record<Lakara, Bi> = {
    lat: { hi: 'वर्तमान के प्रत्यय ति, सि, मि', en: 'the present endings ति, सि, मि' },
    lan: { hi: 'आरम्भ में अ (अट्)', en: 'the augment अ at the start' },
    lrt: { hi: 'स्य / इष्य भविष्यत् चिह्न', en: 'the future marker स्य / इष्य' },
    lot: { hi: 'तु, आनि, ताम्... आज्ञा के प्रत्यय', en: 'imperative endings like तु, आनि, ताम्' },
    vidhilin: { hi: 'ए + त् / युः (ईत्)', en: 'ए followed by त् / युः (from ई)' }
} as Record<Lakara, Bi>

/** लकार पहचान: which lakāra is this form in? */
export const lakaraIdentifyQuestion = (rand: Rand, scope: VerbScope = {}): PracticeQuestion => {
    const lakaras: Lakara[] = scope.lakaras ?? ((scope.level ?? 1) >= 3 ? ['lat', 'lrt', 'lan', 'lot', 'vidhilin'] : ['lat', 'lrt', 'lan'])
    const { d, pada, lakara, paradigm, p, v } = pickCell(rand, { ...scope, lakaras })
    const form = paradigm.forms[p][v][0]
    const label = (l: Lakara): Bi => ({ hi: `${LAKARA_LABEL[l].sa} (${LAKARA_LABEL[l].hi})`, en: `${LAKARA_LABEL[l].sa} (${LAKARA_LABEL[l].en})` })
    const pool: Lakara[] = ['lat', 'lrt', 'lan', 'lot', 'vidhilin']
    const clue = LAKARA_CLUE[lakara] ?? LAKARA_CLUE.lat

    return mcq(rand, {
        key: `li:${d.id}:${lakara}:${pada}:${p}:${v}`,
        q: { hi: 'यह रूप किस लकार में है?', en: 'Which lakāra is this form in?' },
        sa: form,
        explain: {
            hi: `${form} = ${d.root}, ${LAKARA_LABEL[lakara].sa}। पहचान: ${clue.hi}।`,
            en: `${form} = ${d.root}, ${LAKARA_LABEL[lakara].sa}. Clue: ${clue.en}.`
        },
        skill: 'lakara',
        item: `lakara:${d.id}:${lakara}`
    }, label(lakara), seededShuffle(pool.filter(l => l !== lakara), seedOf(rand)).map(label))
}

// ── Nouns ─────────────────────────────────────────────────────────────────────────────────────────────────────────

const NOUN_POOL = () => VOCABULARY.filter(v => v.pos === 'noun' && declinable(v) && v.status === 'verified')

const BASIC_CLASSES = ['a-m', 'a-n', 'aa-f']

const nounPool = (level: number) => NOUN_POOL().filter(v => level >= 2 || BASIC_CLASSES.includes(v.cls as string))

/** A case form's distractors: the other numbers of the same case first, then the same number in other cases. */
const caseDistractors = (rand: Rand, table: ReturnType<typeof declineStem>, vib: Vibhakti, vac: Vacana): string[] => {
    const correct = table[vib][vac]
    const near = VACANAS.filter(x => x !== vac).map(x => table[vib][x][0])
    const far = seededShuffle(CASE_VIBS.filter(x => x !== vib), seedOf(rand)).map(x => table[x][vac][0])

    return [...seededShuffle(near, seedOf(rand)), ...far].filter(f => f && !correct.includes(f))
}

/** शब्दरूप साधना: produce a case form. */
export const nounFormQuestion = (rand: Rand, opts: { level?: number; vibhaktis?: Vibhakti[]; vacanas?: Vacana[] } = {}): PracticeQuestion => {
    const level = opts.level ?? 1
    const v = pick(nounPool(level), rand)
    const table = declineStem(v.stem as string, v.cls!)
    const vibs: Vibhakti[] = opts.vibhaktis ?? (level === 1 ? [1, 2] : level === 2 ? [1, 2, 3, 4, 5] : [1, 2, 3, 4, 5, 6, 7, 8])
    const vib = pick(vibs, rand)
    const vac = pick(opts.vacanas ?? VACANAS, rand)
    const correct = table[vib][vac][0]

    return mcq(rand, {
        key: `nf:${v.id}:${vib}:${vac}`,
        q: {
            hi: `${v.stem} (${v.meaning.hi}): ${VIBHAKTI_LABEL[vib].sa}, ${VACANA_LABEL[vac].hi}?`,
            en: `${v.stem} (${v.meaning.en}): ${VIBHAKTI_LABEL[vib].sa} (${VIBHAKTI_LABEL[vib].en}), ${VACANA_LABEL[vac].en.toLowerCase()}?`
        },
        explain: {
            hi: `${VIBHAKTI_LABEL[vib].sa} ${VACANA_LABEL[vac].hi}: ${correct}। ${VIBHAKTI_LABEL[vib].role.hi}।`,
            en: `${VIBHAKTI_LABEL[vib].sa}, ${VACANA_LABEL[vac].en.toLowerCase()}: ${correct}. ${VIBHAKTI_LABEL[vib].role.en}.`
        },
        skill: 'declension',
        item: `noun:${v.id}:${vib}:${vac}`
    }, correct, caseDistractors(rand, table, vib, vac))
}

interface CaseTemplate {
    before: string
    after: string
    stem: string
    vocabId: string
    vib: Vibhakti
    vac: Vacana
    meaning: Bi
}

const CASE_TEMPLATES: CaseTemplate[] = [
    { before: 'बालकः', after: 'पठति।', stem: 'पुस्तक', vocabId: 'pustakam', vib: 2, vac: 'eka', meaning: { hi: 'बालक पुस्तक पढ़ता है।', en: 'The boy reads a book.' } },
    { before: 'सः', after: 'गच्छति।', stem: 'ग्राम', vocabId: 'gramah', vib: 2, vac: 'eka', meaning: { hi: 'वह गाँव जाता है।', en: 'He goes to the village.' } },
    { before: 'अहं', after: 'लिखामि।', stem: 'लेखनी', vocabId: 'lekhani', vib: 3, vac: 'eka', meaning: { hi: 'मैं कलम से लिखता हूँ।', en: 'I write with a pen.' } },
    { before: 'वयं', after: 'पश्यामः।', stem: 'नेत्र', vocabId: 'netram', vib: 3, vac: 'dvi', meaning: { hi: 'हम आँखों से देखते हैं।', en: 'We see with our (two) eyes.' } },
    { before: 'माता', after: 'फलं ददाति।', stem: 'पुत्र', vocabId: 'putrah', vib: 4, vac: 'eka', meaning: { hi: 'माँ पुत्र को फल देती है।', en: 'Mother gives a fruit to her son.' } },
    { before: '', after: 'पत्रं पतति।', stem: 'वृक्ष', vocabId: 'vrksah', vib: 5, vac: 'eka', meaning: { hi: 'पेड़ से पत्ता गिरता है।', en: 'A leaf falls from the tree.' } },
    { before: 'बालकः', after: 'निर्गच्छति।', stem: 'गृह', vocabId: 'grham', vib: 5, vac: 'eka', meaning: { hi: 'बालक घर से निकलता है।', en: 'The boy goes out of the house.' } },
    { before: 'एतत्', after: 'पुस्तकम्।', stem: 'राम', vocabId: 'ramah', vib: 6, vac: 'eka', meaning: { hi: 'यह राम की पुस्तक है।', en: 'This is Rāma’s book.' } },
    { before: '', after: 'खगाः सन्ति।', stem: 'वृक्ष', vocabId: 'vrksah', vib: 7, vac: 'eka', meaning: { hi: 'पेड़ पर पक्षी हैं।', en: 'There are birds in the tree.' } },
    { before: 'मत्स्यः', after: 'वसति।', stem: 'जल', vocabId: 'jalam', vib: 7, vac: 'eka', meaning: { hi: 'मछली पानी में रहती है।', en: 'The fish lives in water.' } },
    { before: 'बालकाः', after: 'क्रीडन्ति।', stem: 'उद्यान', vocabId: 'udyanam', vib: 7, vac: 'eka', meaning: { hi: 'बालक बगीचे में खेलते हैं।', en: 'The boys play in the garden.' } },
    { before: '', after: 'नमः।', stem: 'गुरु', vocabId: 'guruh', vib: 4, vac: 'eka', meaning: { hi: 'गुरु को नमस्कार।', en: 'Salutation to the teacher.' } },
    { before: 'पुत्रः', after: 'सह गच्छति।', stem: 'पितृ', vocabId: 'pita', vib: 3, vac: 'eka', meaning: { hi: 'पुत्र पिता के साथ जाता है।', en: 'The son goes with his father.' } }
]

/** How each case is said in English and Hindi around a noun, for the "which form means …" questions. */
const CASE_PHRASE: Record<Vibhakti, (n: string) => string> = {
    1: n => `${n} (as the subject)`,
    2: n => `${n} (as the object)`,
    3: n => `with / by ${n}`,
    4: n => `to / for ${n}`,
    5: n => `from ${n}`,
    6: n => `of ${n}`,
    7: n => `in / on ${n}`,
    8: n => `O ${n}! (calling out)`
}

const CASE_PHRASE_HI: Record<Vibhakti, (n: string) => string> = {
    1: n => `${n} (कर्ता)`,
    2: n => `${n} को (कर्म)`,
    3: n => `${n} से / के द्वारा`,
    4: n => `${n} के लिए`,
    5: n => `${n} से (अलग होकर)`,
    6: n => `${n} का / के / की`,
    7: n => `${n} में / पर`,
    8: n => `हे ${n}!`
}

const NUMBER_TAG: Record<Vacana, Bi> = {
    eka: { hi: 'एक', en: 'one' },
    dvi: { hi: 'दो', en: 'two' },
    bahu: { hi: 'तीन या अधिक', en: 'three or more' }
}

/** विभक्ति चयन: a sentence with a gap, or "which form means 'from the tree' (two)?". */
export const vibhaktiChoiceQuestion = (rand: Rand, scope: TopicScope = {}, level = 3): PracticeQuestion => {
    const templates = CASE_TEMPLATES.filter(tpl => (!scope.vibhaktis || scope.vibhaktis.includes(tpl.vib)) && (!scope.vacanas || scope.vacanas.includes(tpl.vac)))

    if (templates.length > 0 && rand() < 0.2) {
        const tpl = pick(templates, rand)
        const entry = getVocab(tpl.vocabId)
        const table = entry?.paradigmId ? getNounParadigm(entry.paradigmId)?.table : entry?.cls ? declineStem(entry.stem as string, entry.cls) : null

        if (table) {
            const correct = table[tpl.vib][tpl.vac][0]
            const others = seededShuffle(CASE_VIBS.filter(v => v !== tpl.vib), seedOf(rand)).map(v => table[v][tpl.vac][0]).filter(f => f !== correct)
            const sentence = `${tpl.before ? `${tpl.before} ` : ''}___ ${tpl.after}`

            return mcq(rand, {
                key: `vc:${tpl.vocabId}:${tpl.vib}:${tpl.after}`,
                q: { hi: `वाक्य पूरा कीजिए: "${tpl.meaning.hi}"`, en: `Complete the sentence: "${tpl.meaning.en}"` },
                sa: sentence,
                explain: {
                    hi: `यहाँ ${VIBHAKTI_LABEL[tpl.vib].sa} चाहिए (${VIBHAKTI_LABEL[tpl.vib].role.hi}): ${correct}।`,
                    en: `This needs ${VIBHAKTI_LABEL[tpl.vib].sa} (${VIBHAKTI_LABEL[tpl.vib].role.en.toLowerCase()}): ${correct}.`
                },
                skill: 'vibhakti',
                item: `vibhakti:${tpl.vocabId}:${tpl.vib}`,
                playAudio: false
            }, correct, others)
        }
    }

    const v = pick(nounPool(level), rand)
    const table = declineStem(v.stem as string, v.cls!)
    const vibs: Vibhakti[] = scope.vibhaktis ?? (level === 1 ? [1, 2, 3, 4] : level === 2 ? CASE_VIBS : ALL_VIBS)
    const vib = pick(vibs, rand)
    const vac = pick(scope.vacanas ?? VACANAS, rand)
    const correct = table[vib][vac][0]
    const others = seededShuffle(ALL_VIBS.filter(x => x !== vib), seedOf(rand)).map(x => table[x][vac][0]).filter(f => f && !table[vib][vac].includes(f))
    const en = CASE_PHRASE[vib](vib === 8 ? firstSense(v.meaning.en) : `the ${firstSense(v.meaning.en)}`)
    const hi = CASE_PHRASE_HI[vib](firstSense(v.meaning.hi))

    return mcq(rand, {
        key: `vg:${v.id}:${vib}:${vac}`,
        q: {
            hi: `किस रूप का अर्थ है "${hi}" (${NUMBER_TAG[vac].hi})?`,
            en: `Which form means "${en}" (${NUMBER_TAG[vac].en})?`
        },
        explain: {
            hi: `${VIBHAKTI_LABEL[vib].sa} (${VIBHAKTI_LABEL[vib].marker.hi}), ${VACANA_LABEL[vac].hi}: ${correct}।`,
            en: `${VIBHAKTI_LABEL[vib].sa} (${VIBHAKTI_LABEL[vib].marker.en}), ${VACANA_LABEL[vac].en.toLowerCase()}: ${correct}.`
        },
        skill: 'vibhakti',
        item: `vibhakti:${v.id}:${vib}`,
        playAudio: false
    }, correct, others)
}

/** द्विवचन चुनौती: turn a singular into a dual (a noun in any case, or a verb in any person). */
export const dualQuestion = (rand: Rand, scope: TopicScope = {}, level = 1): PracticeQuestion => {
    const verbTopics = Boolean(scope.lakaras || scope.purushas)
    const nounTopics = Boolean(scope.vibhaktis)
    const noun = nounTopics && !verbTopics ? true : verbTopics && !nounTopics ? false : rand() < 0.5

    if (noun) {
        const v = pick(nounPool(level), rand)
        const table = declineStem(v.stem as string, v.cls!)
        const vibs: Vibhakti[] = scope.vibhaktis ?? (level === 1 ? [1, 2] : level === 2 ? [1, 2, 3, 4, 5] : CASE_VIBS)
        const vib = pick(vibs, rand)
        const sg = table[vib].eka[0]
        const du = table[vib].dvi[0]
        const pl = table[vib].bahu[0]
        const otherVib = pick(CASE_VIBS.filter(x => x !== vib), rand)

        return mcq(rand, {
            key: `du:n:${v.id}:${vib}`,
            q: { hi: `दो के लिए (द्विवचन) रूप चुनिए: ${VIBHAKTI_LABEL[vib].sa}`, en: `Choose the form for exactly two (dual), ${VIBHAKTI_LABEL[vib].sa}` },
            sa: sg,
            explain: { hi: `${sg} → ${du} (दो)। ${pl} तीन या अधिक के लिए है।`, en: `${sg} → ${du} (two). ${pl} is for three or more.` },
            skill: 'declension',
            item: 'concept:dvivacana'
        }, du, [pl, table[otherVib].dvi[0], sg].filter(f => f !== du))
    }

    const { d, pada, lakara, paradigm, p } = pickCell(rand, { level, lakaras: scope.lakaras, purushas: scope.purushas, vacanas: ['dvi'] })
    const sg = paradigm.forms[p].eka[0]
    const du = paradigm.forms[p].dvi[0]
    const other: Purusha = p === 'uttama' ? 'prathama' : 'uttama'

    return mcq(rand, {
        key: `du:v:${d.id}:${lakara}:${pada}:${p}`,
        q: { hi: `इस क्रिया का द्विवचन रूप? (${LAKARA_LABEL[lakara].sa})`, en: `What is the dual of this verb? (${LAKARA_LABEL[lakara].sa})` },
        sa: sg,
        explain: { hi: `${sg} → ${du}: ${whoOf(p, 'dvi').hi}।`, en: `${sg} → ${du}: ${whoOf(p, 'dvi').en}.` },
        skill: 'conjugation',
        item: `verb:${d.id}:${lakara}:${pada}:${p}:dvi`
    }, du, [paradigm.forms[p].bahu[0], paradigm.forms[other].dvi[0], sg].filter(f => f !== du))
}

// ── Sandhi ────────────────────────────────────────────────────────────────────────────────────────────────────────

/** Words that end in a vowel as written: feminine nominatives, duals, locatives and indeclinables of the course. */
const SANDHI_LEFT = [
    'च', 'अपि', 'इति', 'अत्र', 'तत्र', 'कुत्र', 'सदा', 'अद्य', 'यदा', 'तदा', 'कदा', 'एव', 'न', 'इह', 'तथा', 'यथा', 'सह', 'विना',
    'बालिका', 'माता', 'पिता', 'नदी', 'छात्रा', 'कथा', 'विद्या', 'सा', 'लता', 'गङ्गा', 'देवी', 'नारी', 'भगिनी', 'पुत्री', 'सीता',
    'बालकौ', 'छात्रौ', 'तौ', 'ते', 'फले', 'वने', 'गृहे', 'ग्रामे', 'नगरे', 'वृक्षे', 'जले', 'उद्याने', 'विद्यालये', 'मधु', 'गुरो'
]

/** Words that begin with a vowel. */
const SANDHI_RIGHT = ['अपि', 'इति', 'एव', 'अत्र', 'अद्य', 'इदानीम्', 'उपरि', 'अस्ति', 'आगच्छति', 'इच्छति', 'उपविशति', 'उत्तिष्ठति', 'एकदा', 'आसीत्', 'अहम्', 'अधुना', 'एतत्', 'ओदनम्', 'आम्रम्']

interface GeneratedSandhi {
    left: string
    right: string
    result: string
    rule: VowelRule
}

let generatedSandhi: GeneratedSandhi[] | null = null

/** Every vowel sandhi the engine makes between SANDHI_LEFT and SANDHI_RIGHT, built once. */
const GENERATED_SANDHI = (): GeneratedSandhi[] => {
    if (generatedSandhi) return generatedSandhi

    const out: GeneratedSandhi[] = []
    const seen = new Set<string>()

    for (const left of SANDHI_LEFT) {
        for (const right of SANDHI_RIGHT) {
            if (left === right) continue
            const joined = combineVowels(left, right)
            if (!joined || !joined.result || joined.result === `${left}${right}`) continue
            const id = `${left}+${right}`
            if (seen.has(id)) continue
            seen.add(id)
            out.push({ left, right, result: joined.result, rule: joined.rule })
        }
    }

    generatedSandhi = out
    return out
}

/** Results made by exactly one pair, so a split question has one right answer. */
const SPLITTABLE = (): GeneratedSandhi[] => {
    const all = GENERATED_SANDHI()
    const count = new Map<string, number>()
    all.forEach(s => count.set(s.result, (count.get(s.result) ?? 0) + 1))
    return all.filter(s => count.get(s.result) === 1)
}

const VOWEL_SWAP: Record<string, string> = { 'अ': 'आ', 'आ': 'अ', 'इ': 'ई', 'ई': 'इ', 'उ': 'ऊ', 'ऊ': 'उ', 'ए': 'इ', 'ओ': 'उ', 'ऐ': 'ए', 'औ': 'ओ' }

const rightVariant = (word: string): string | null => (VOWEL_SWAP[word[0]] ? VOWEL_SWAP[word[0]] + word.slice(1) : null)

const leftVariant = (word: string): string | null => {
    if (word.endsWith('ा')) return word.slice(0, -1)
    if (word.endsWith('ी')) return `${word.slice(0, -1)}ि`
    if (word.endsWith('े')) return `${word.slice(0, -1)}ै`
    if (word.endsWith('ौ')) return `${word.slice(0, -1)}ो`
    return `${word}ा`
}

const exampleKind = (e: SandhiExample): SandhiKind => e.kind

const inSandhiScope = (kind: SandhiKind, scope: TopicScope): boolean => !scope.sandhi || scope.sandhi.includes(kind)

/** सन्धि निर्माता: combine two words. */
export const sandhiCombineQuestion = (rand: Rand, scope: TopicScope = {}): PracticeQuestion => {
    const examples = ALL_SANDHI_EXAMPLES.filter(e => inSandhiScope(exampleKind(e), scope))
    const generated = GENERATED_SANDHI().filter(s => inSandhiScope(s.rule, scope))

    if (examples.length > 0 && (generated.length === 0 || rand() < 0.3)) {
        const ex = pick(examples, rand)
        const wrong = seededShuffle(ALL_SANDHI_EXAMPLES.filter(e => e.id !== ex.id && e.left[0] === ex.left[0]), seedOf(rand)).map(e => e.result)
        const naive = ex.left.replace(/[ःम्]$/, '') + ex.right

        return mcq(rand, {
            key: `sc:${ex.left}+${ex.right}`,
            q: { hi: 'सन्धि करके एक शब्द बनाइए', en: 'Join these with sandhi' },
            sa: `${ex.left} + ${ex.right}`,
            explain: { hi: `${ex.left} + ${ex.right} = ${ex.result}। ${ex.steps.hi}`, en: `${ex.left} + ${ex.right} = ${ex.result}. ${ex.steps.en}` },
            skill: 'sandhi',
            item: `sandhi:${ex.id}`,
            playAudio: false
        }, ex.result, [...wrong, naive, ex.left + ' ' + ex.right])
    }

    const s = pick(generated, rand)
    const lv = leftVariant(s.left)
    const rv = rightVariant(s.right)
    const wrong = [
        lv ? combineVowels(lv, s.right)?.result : undefined,
        rv ? combineVowels(s.left, rv)?.result : undefined,
        `${s.left}${s.right}`,
        `${s.left} ${s.right}`
    ].filter((w): w is string => Boolean(w) && w !== s.result)
    const rule = VOWEL_RULES[s.rule]

    return mcq(rand, {
        key: `sc:${s.left}+${s.right}`,
        q: { hi: 'सन्धि करके एक शब्द बनाइए', en: 'Join these with sandhi' },
        sa: `${s.left} + ${s.right}`,
        explain: { hi: `${s.left} + ${s.right} = ${s.result}। ${rule.name.hi}: ${rule.text.hi}`, en: `${s.left} + ${s.right} = ${s.result}. ${rule.name.en}: ${rule.text.en}` },
        skill: 'sandhi',
        item: `sandhi:${s.rule}`,
        playAudio: false
    }, s.result, wrong)
}

/** सन्धि विच्छेद: split a joined word. */
export const sandhiSplitQuestion = (rand: Rand, scope: TopicScope = {}): PracticeQuestion => {
    const examples = ALL_SANDHI_EXAMPLES.filter(e => inSandhiScope(exampleKind(e), scope))
    const generated = SPLITTABLE().filter(s => inSandhiScope(s.rule, scope))

    if (examples.length > 0 && (generated.length === 0 || rand() < 0.3)) {
        const ex = pick(examples, rand)
        const correct = `${ex.left} + ${ex.right}`
        const distractors = (VOWEL_EXAMPLES.includes(ex) ? VOWEL_EXAMPLES : ALL_SANDHI_EXAMPLES).filter(e => e.id !== ex.id).map(e => `${e.left} + ${e.right}`)
        const altLeft = ex.left.endsWith('ा') ? ex.left.slice(0, -1) : `${ex.left}ा`

        return mcq(rand, {
            key: `ss:${ex.left}+${ex.right}`,
            q: { hi: 'इस शब्द का सन्धि-विच्छेद?', en: 'Split this word (सन्धि-विच्छेद)' },
            sa: ex.result,
            explain: { hi: `${ex.result} = ${correct}। ${ex.steps.hi}`, en: `${ex.result} = ${correct}. ${ex.steps.en}` },
            skill: 'sandhi',
            item: `sandhi:${ex.id}`,
            playAudio: false
        }, correct, [`${altLeft} + ${ex.right}`, ...seededShuffle(distractors, seedOf(rand))])
    }

    const s = pick(generated, rand)
    const correct = `${s.left} + ${s.right}`
    const lv = leftVariant(s.left)
    const rv = rightVariant(s.right)
    const wrong = [lv && `${lv} + ${s.right}`, rv && `${s.left} + ${rv}`, lv && rv && `${lv} + ${rv}`].filter((w): w is string => Boolean(w) && w !== correct)
    const rule = VOWEL_RULES[s.rule]

    return mcq(rand, {
        key: `ss:${s.left}+${s.right}`,
        q: { hi: 'इस शब्द का सन्धि-विच्छेद?', en: 'Split this word (सन्धि-विच्छेद)' },
        sa: s.result,
        explain: { hi: `${s.result} = ${correct}। ${rule.name.hi}: ${rule.text.hi}`, en: `${s.result} = ${correct}. ${rule.name.en}: ${rule.text.en}` },
        skill: 'sandhi',
        item: `sandhi:${s.rule}`,
        playAudio: false
    }, correct, wrong)
}

/** समास पहचान: the type of a compound, the compound for an analysis, or the analysis of a compound. */
export const samasaQuestion = (rand: Rand, scope: TopicScope = {}): PracticeQuestion => {
    const pool = SAMASA_EXAMPLES.filter(e => !scope.samasa || scope.samasa.includes(e.type))
    const ex = pick(pool.length > 0 ? pool : SAMASA_EXAMPLES, rand)
    const label = (id: keyof typeof SAMASA_TYPES): Bi => SAMASA_TYPES[id].name
    const others = SAMASA_EXAMPLES.filter(e => e.id !== ex.id)
    const sameType = seededShuffle(others.filter(e => e.type === ex.type), seedOf(rand))
    const rest = seededShuffle(others.filter(e => e.type !== ex.type), seedOf(rand))
    const explain: Bi = { hi: `${ex.compound} = ${ex.vigraha}: ${SAMASA_TYPES[ex.type].name.hi}। ${SAMASA_TYPES[ex.type].test.hi}`, en: `${ex.compound} = ${ex.vigraha}: ${SAMASA_TYPES[ex.type].name.en}. ${SAMASA_TYPES[ex.type].test.en}` }
    const r = rand()

    if (r < 0.34) {
        return mcq(rand, {
            key: `smc:${ex.id}`,
            q: { hi: `इस विग्रह से कौन सा समास बनता है? (${SAMASA_TYPES[ex.type].name.hi})`, en: `Which compound does this analysis make? (${SAMASA_TYPES[ex.type].name.en})` },
            sa: ex.vigraha,
            explain,
            skill: 'sandhi',
            item: `samasa:${ex.id}`,
            playAudio: false
        }, ex.compound, [...sameType, ...rest].map(e => e.compound))
    }

    if (r < 0.67) {
        return mcq(rand, {
            key: `smv:${ex.id}`,
            q: { hi: 'इस समास का विग्रह क्या है?', en: 'What is the analysis (vigraha) of this compound?' },
            sa: ex.compound,
            explain,
            skill: 'sandhi',
            item: `samasa:${ex.id}`,
            playAudio: false
        }, ex.vigraha, [...sameType, ...rest].map(e => e.vigraha))
    }

    const types = (Object.keys(SAMASA_TYPES) as Array<keyof typeof SAMASA_TYPES>).filter(k => k !== ex.type)

    return mcq(rand, {
        key: `sm:${ex.id}`,
        q: { hi: `यह कौन सा समास है? (विग्रह: ${ex.vigraha})`, en: `Which kind of compound is this? (analysis: ${ex.vigraha})` },
        sa: ex.compound,
        explain,
        skill: 'sandhi',
        item: `samasa:${ex.id}`,
        playAudio: false
    }, label(ex.type), seededShuffle(types, seedOf(rand)).map(label))
}

// ── Vocabulary, listening and letters ─────────────────────────────────────────────────────────────────────────────

const VOCAB_POOL = (level = 3) => VOCABULARY.filter(v => v.level <= level && v.pos !== 'phrase' && v.meaning.en.length < 40)

/** Vocabulary: Sanskrit → meaning. */
export const vocabMeaningQuestion = (rand: Rand, level = 3): PracticeQuestion => {
    const pool = VOCAB_POOL(level)
    const v = pick(pool, rand)
    const sameCat = pool.filter(x => x.category === v.category && x.id !== v.id && x.meaning.en !== v.meaning.en)
    const others = seededShuffle(sameCat.length >= 3 ? sameCat : pool.filter(x => x.id !== v.id), seedOf(rand)).map(x => x.meaning)

    return mcq(rand, {
        key: `vm:${v.id}`,
        q: { hi: 'इसका अर्थ क्या है?', en: 'What does this word mean?' },
        sa: v.sa,
        explain: { hi: `${v.sa} = ${v.meaning.hi}`, en: `${v.sa} = ${v.meaning.en}` },
        skill: 'vocabulary',
        item: v.id
    }, v.meaning, others)
}

/** Vocabulary: meaning → Sanskrit. */
export const vocabReverseQuestion = (rand: Rand, level = 3): PracticeQuestion => {
    const pool = VOCAB_POOL(level)
    const v = pick(pool, rand)
    const others = seededShuffle(pool.filter(x => x.id !== v.id && x.category === v.category), seedOf(rand)).map(x => x.sa)

    return mcq(rand, {
        key: `vr:${v.id}`,
        q: { hi: `"${v.meaning.hi}" के लिए संस्कृत शब्द?`, en: `Which Sanskrit word means "${v.meaning.en}"?` },
        explain: { hi: `${v.meaning.hi} = ${v.sa}`, en: `${v.meaning.en} = ${v.sa}` },
        skill: 'vocabulary',
        item: v.id
    }, v.sa, others.length >= 3 ? others : seededShuffle(pool.filter(x => x.id !== v.id).map(x => x.sa), seedOf(rand)))
}

/** श्रवण अभ्यास: hear a verb form, a word or a noun form and pick it. */
export const listeningQuestion = (rand: Rand, level = 3, scope: TopicScope = {}): PracticeQuestion => {
    const verbTopics = Boolean(scope.lakaras || scope.purushas || scope.vacanas)

    if (verbTopics || rand() < 0.5) {
        const { paradigm, p, v, d, lakara, pada } = pickCell(rand, { level: level >= 4 ? 3 : 1, lakaras: scope.lakaras, purushas: scope.purushas, vacanas: scope.vacanas })
        const form = paradigm.forms[p][v][0]
        const near = [
            paradigm.forms[p][v === 'eka' ? 'bahu' : 'eka'][0],
            paradigm.forms[p === 'uttama' ? 'prathama' : 'uttama'][v][0],
            paradigm.forms[p][v === 'dvi' ? 'eka' : 'dvi'][0]
        ].filter(f => f && f !== form)

        return { kind: 'listen', key: `ls:v:${d.id}:${lakara}:${pada}:${p}:${v}`, q: { hi: 'आपने कौन सा रूप सुना?', en: 'Which form did you hear?' }, sa: form, options: seededShuffle(Array.from(new Set([form, ...near])).slice(0, 4), seedOf(rand)), answer: form, skill: 'listening', item: `verb:${d.id}:${lakara}:${pada}:${p}:${v}` }
    }

    if (rand() < 0.5) {
        const noun = pick(nounPool(2), rand)
        const table = declineStem(noun.stem as string, noun.cls!)
        const vib = pick(CASE_VIBS, rand)
        const vac = pick(VACANAS, rand)
        const form = table[vib][vac][0]
        const near = caseDistractors(rand, table, vib, vac).slice(0, 2)

        return { kind: 'listen', key: `ls:n:${form}`, q: { hi: 'आपने कौन सा रूप सुना?', en: 'Which form did you hear?' }, sa: form, options: seededShuffle(Array.from(new Set([form, ...near])), seedOf(rand)), answer: form, skill: 'listening', item: `noun:${noun.id}:${vib}:${vac}` }
    }

    const pool = VOCAB_POOL(level).filter(x => x.pos === 'noun')
    const w = pick(pool, rand)
    const similar = seededShuffle(pool.filter(x => x.id !== w.id && (x.sa[0] === w.sa[0] || x.category === w.category)), seedOf(rand)).slice(0, 2).map(x => x.sa)

    return { kind: 'listen', key: `ls:w:${w.sa}`, q: { hi: 'आपने कौन सा शब्द सुना?', en: 'Which word did you hear?' }, sa: w.sa, options: seededShuffle([w.sa, ...similar], seedOf(rand)), answer: w.sa, skill: 'listening', item: w.id }
}

const DECOYS = ['क', 'म', 'रा', 'ति', 'ना', 'वः', 'स्य', 'णि', 'भ्यः', 'षु']

/** अक्षर निर्माण: build a word, or a case form of a noun, from its aksharas. */
export const aksharaQuestion = (rand: Rand, level = 2, scope: TopicScope = {}): PracticeQuestion => {
    const caseTopics = Boolean(scope.vibhaktis || scope.vacanas)

    if (caseTopics || rand() < 0.5) {
        const noun = pick(nounPool(level).filter(x => aksharas(x.stem as string).length <= 4), rand)
        const table = declineStem(noun.stem as string, noun.cls!)
        const vib = pick(scope.vibhaktis ?? CASE_VIBS, rand)
        const vac = pick(scope.vacanas ?? VACANAS, rand)
        const form = table[vib][vac][0]
        const pieces = aksharas(form)
        const decoy = pick(DECOYS.filter(d => !pieces.includes(d)), rand)

        if (pieces.length >= 2 && pieces.length <= 7) {
            return {
                kind: 'arrange',
                key: `ak:${form}`,
                prompt: {
                    hi: `अक्षरों से रूप बनाइए: "${firstSense(noun.meaning.hi)}", ${VIBHAKTI_LABEL[vib].sa}, ${VACANA_LABEL[vac].hi}`,
                    en: `Build "${firstSense(noun.meaning.en)}" in ${VIBHAKTI_LABEL[vib].sa} (${VIBHAKTI_LABEL[vib].en.toLowerCase()}), ${VACANA_LABEL[vac].en.toLowerCase()}`
                },
                pieces: [...pieces, decoy],
                answer: form,
                skill: 'devanagari',
                item: `noun:${noun.id}:${vib}:${vac}`
            }
        }
    }

    const pool = VOCAB_POOL(level).filter(x => x.pos === 'noun' && aksharas(x.sa).length >= 2 && aksharas(x.sa).length <= 6)
    const w = pick(pool, rand)
    const pieces = aksharas(w.sa)
    const decoy = pick(DECOYS.filter(d => !pieces.includes(d)), rand)

    return {
        kind: 'arrange',
        key: `ak:${w.sa}`,
        prompt: { hi: `अक्षरों से शब्द बनाइए: "${w.meaning.hi}"`, en: `Build the word for "${w.meaning.en}"` },
        pieces: [...pieces, decoy],
        answer: w.sa,
        skill: 'devanagari',
        item: w.id
    }
}

// ── Sentences and stories ─────────────────────────────────────────────────────────────────────────────────────────

interface SentenceTemplate {
    words: string[]
    distractors: string[]
    meaning: Bi
    orderFree?: boolean
}

const SENTENCES: SentenceTemplate[] = [
    { words: ['अहं', 'पठामि'], distractors: ['पठति', 'पठसि'], meaning: { hi: 'मैं पढ़ता हूँ।', en: 'I read.' } },
    { words: ['त्वं', 'लिखसि'], distractors: ['लिखति', 'लिखामि'], meaning: { hi: 'तुम लिखते हो।', en: 'You write.' } },
    { words: ['सः', 'गच्छति'], distractors: ['गच्छामि', 'गच्छन्ति'], meaning: { hi: 'वह जाता है।', en: 'He goes.' } },
    { words: ['बालकौ', 'क्रीडतः'], distractors: ['क्रीडन्ति', 'क्रीडति', 'बालकाः'], meaning: { hi: 'दो बालक खेलते हैं।', en: 'Two boys play.' } },
    { words: ['बालिकाः', 'हसन्ति'], distractors: ['हसतः', 'हसति', 'बालिके'], meaning: { hi: 'बालिकाएँ हँसती हैं।', en: 'The girls laugh.' } },
    { words: ['वयं', 'जलं', 'पिबामः'], distractors: ['पिबावः', 'पिबन्ति'], meaning: { hi: 'हम पानी पीते हैं।', en: 'We drink water.' } },
    { words: ['बालकः', 'पुस्तकं', 'पठति'], distractors: ['पठामि', 'पुस्तकानि'], meaning: { hi: 'बालक पुस्तक पढ़ता है।', en: 'The boy reads a book.' } },
    { words: ['वानरः', 'फलं', 'खादति'], distractors: ['खादन्ति', 'फलानि'], meaning: { hi: 'बंदर फल खाता है।', en: 'The monkey eats a fruit.' } },
    { words: ['माता', 'ओदनं', 'पचति'], distractors: ['पचामि', 'माताः'], meaning: { hi: 'माँ भात पकाती है।', en: 'Mother cooks rice.' } },
    { words: ['आवां', 'ग्रामं', 'गच्छावः'], distractors: ['गच्छामः', 'ग्रामः'], meaning: { hi: 'हम दोनों गाँव जाते हैं।', en: 'We two go to the village.' } },
    { words: ['यूयं', 'चन्द्रं', 'पश्यथ'], distractors: ['पश्यथः', 'पश्यन्ति'], meaning: { hi: 'तुम सब चाँद देखते हो।', en: 'You all see the moon.' } },
    { words: ['सा', 'गीतं', 'गायति'], distractors: ['गायन्ति', 'सः'], meaning: { hi: 'वह गीत गाती है।', en: 'She sings a song.' } },
    { words: ['माता', 'पिता', 'च', 'गच्छतः'], distractors: ['गच्छति', 'गच्छन्ति'], meaning: { hi: 'माता और पिता जाते हैं।', en: 'Mother and father go.' } },
    { words: ['अहं', 'भ्राता', 'च', 'पिबावः'], distractors: ['पिबतः', 'पिबामः'], meaning: { hi: 'मैं और भाई पीते हैं।', en: 'My brother and I drink.' } },
    { words: ['शिक्षकः', 'कक्षां', 'प्रविशति'], distractors: ['प्रविशन्ति', 'कक्षा'], meaning: { hi: 'शिक्षक कक्षा में प्रवेश करते हैं।', en: 'The teacher enters the classroom.' } },
    { words: ['सः', 'ग्रामम्', 'अगच्छत्'], distractors: ['गमिष्यति', 'ग्रामः'], meaning: { hi: 'वह गाँव गया।', en: 'He went to the village.' } },
    { words: ['वयं', 'श्वः', 'क्रीडिष्यामः'], distractors: ['क्रीडामः', 'क्रीडिष्यन्ति'], meaning: { hi: 'कल हम खेलेंगे।', en: 'Tomorrow we will play.' } },
    { words: ['अहं', 'न', 'जानामि'], distractors: ['जानाति', 'जानीमः'], meaning: { hi: 'मैं नहीं जानता।', en: 'I do not know.' }, orderFree: false }
]

interface SentenceFrame {
    dhatu: string
    objects: string[]
    /** The English verb and its past. */
    en: string
    past: string
    /** Going to a place: "to the village". */
    toPlace?: boolean
    /** Animals can be the subject (eat, drink, see, go). */
    animals?: boolean
}

const FRAMES: SentenceFrame[] = [
    { dhatu: 'path', objects: ['pustakam', 'patram', 'pathah', 'katha'], en: 'read', past: 'read' },
    { dhatu: 'likh', objects: ['patram', 'pathah', 'katha'], en: 'write', past: 'wrote' },
    { dhatu: 'khad', objects: ['phalam', 'odanah', 'modakah', 'amram', 'kadaliphalam'], en: 'eat', past: 'ate', animals: true },
    { dhatu: 'pa', objects: ['jalam', 'dugdham'], en: 'drink', past: 'drank', animals: true },
    { dhatu: 'drs', objects: ['candrah', 'vrksah', 'nadi', 'gajah', 'simhah', 'khagah', 'mayurah'], en: 'see', past: 'saw', animals: true },
    { dhatu: 'gam', objects: ['gramah', 'nagaram', 'vidyalayah', 'udyanam', 'apanah', 'grham', 'mandiram'], en: 'go', past: 'went', toPlace: true, animals: true },
    { dhatu: 'pac', objects: ['odanah', 'sakam'], en: 'cook', past: 'cooked' },
    { dhatu: 'sru', objects: ['gitam', 'katha'], en: 'hear', past: 'heard' },
    { dhatu: 'gai', objects: ['gitam'], en: 'sing', past: 'sang' },
    { dhatu: 'smr', objects: ['katha', 'pathah'], en: 'remember', past: 'remembered' },
    { dhatu: 'jna', objects: ['uttaram', 'katha'], en: 'know', past: 'knew' }
]

/** People who can be the subject of any frame; the plural is spelt out for the English prompt. */
const PEOPLE: [id: string, singular: string, plural: string][] = [
    ['balakah', 'boy', 'boys'], ['balika', 'girl', 'girls'], ['chatrah', 'student', 'students'], ['chatra', 'student', 'students'],
    ['siksakah', 'teacher', 'teachers'], ['sevakah', 'servant', 'servants'], ['putrah', 'son', 'sons'], ['mitram', 'friend', 'friends']
]

const ANIMALS: [id: string, singular: string, plural: string][] = [
    ['vanarah', 'monkey', 'monkeys'], ['gajah', 'elephant', 'elephants'], ['asvah', 'horse', 'horses'], ['mayurah', 'peacock', 'peacocks']
]

const PRONOUN_EN: Record<Purusha, Record<Vacana, { en: string, hi: string }>> = {
    uttama: { eka: { en: 'I', hi: 'मैं' }, dvi: { en: 'we two', hi: 'हम दोनों' }, bahu: { en: 'we', hi: 'हम सब' } },
    madhyama: { eka: { en: 'you', hi: 'तुम' }, dvi: { en: 'you two', hi: 'तुम दोनों' }, bahu: { en: 'you all', hi: 'तुम सब' } },
    prathama: { eka: { en: 'he', hi: 'वह' }, dvi: { en: 'the two of them', hi: 'वे दोनों' }, bahu: { en: 'they', hi: 'वे सब' } }
}

const thirdSingular = (verb: string): string => (/(o|s|sh|ch|x|z)$/.test(verb) ? `${verb}es` : `${verb}s`)

const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

/** A sentence built from a subject, an object in the accusative and a verb that agrees with the subject. */
const builtSentenceQuestion = (rand: Rand, level: number, scope: TopicScope): PracticeQuestion | null => {
    const frame = pick(FRAMES, rand)
    const d = getDhatu(frame.dhatu)
    if (!d) return null

    const allowed: Lakara[] = (scope.lakaras ?? (level >= 3 ? ['lat', 'lrt', 'lan'] : level === 2 ? ['lat', 'lrt'] : ['lat'])).filter(l => ['lat', 'lrt', 'lan'].includes(l))
    if (allowed.length === 0) return null

    const lakara = pick(allowed, rand)
    const paradigm = getParadigm(d.id, lakara, padasOf(d)[0])
    if (!paradigm || paradigm.status !== 'verified') return null

    const p = pick(scope.purushas ?? PURUSHAS, rand)
    const v = pick(scope.vacanas ?? VACANAS, rand)
    const verbForm = paradigm.forms[p]?.[v]?.[0]
    if (!verbForm) return null

    const object = getVocab(pick(frame.objects, rand))
    if (!declinable(object)) return null

    const objectTable = declineStem(object.stem as string, object.cls!)
    const objectForm = objectTable[2].eka[0]

    let subjectSa: string
    let subjectEn: string
    let subjectHi: string

    if (p !== 'prathama' || rand() < 0.3) {
        subjectSa = p === 'prathama' && v !== 'dvi' && rand() < 0.4 ? FEMININE_PRONOUN[v] : PRONOUN[p][v]
        subjectEn = p === 'prathama' && subjectSa === FEMININE_PRONOUN[v] && v === 'eka' ? 'she' : PRONOUN_EN[p][v].en
        subjectHi = PRONOUN_EN[p][v].hi
    }
    else {
        const list = frame.animals && rand() < 0.35 ? ANIMALS : PEOPLE
        const [id, singular, plural] = pick(list, rand)
        const noun = getVocab(id)
        if (!declinable(noun)) return null
        subjectSa = declineStem(noun.stem as string, noun.cls!)[1][v][0]
        subjectEn = v === 'eka' ? `the ${singular}` : v === 'dvi' ? `the two ${plural}` : `the ${plural}`
        subjectHi = `${firstSense(noun.meaning.hi)}${v === 'dvi' ? ' (दो)' : v === 'bahu' ? ' (सब)' : ''}`
    }

    const third = p === 'prathama' && v === 'eka'
    const verbEn = lakara === 'lrt' ? `will ${frame.en}` : lakara === 'lan' ? frame.past : third ? thirdSingular(frame.en) : frame.en
    const objectEn = `${frame.toPlace ? 'to ' : ''}the ${firstSense(object.meaning.en)}`
    const english = `${capitalise(subjectEn)} ${verbEn} ${objectEn}.`

    const words = [medial(subjectSa), medial(objectForm), verbForm]
    const otherVerbs = [
        paradigm.forms[p === 'uttama' ? 'prathama' : 'uttama'][v][0],
        paradigm.forms[p][v === 'eka' ? 'bahu' : 'eka'][0]
    ]
    const objectWrong = object.cls === 'a-n' ? objectTable[1].bahu[0] : objectTable[1].eka[0]
    const distractors = Array.from(new Set([...otherVerbs, medial(objectWrong)])).filter(w => w && !words.includes(w))

    return {
        kind: 'build',
        key: `sb:${words.join('-')}`,
        prompt: {
            hi: `बनाइए: ${subjectHi} · ${firstSense(object.meaning.hi)} · ${firstSense(d.meaning.hi)} (${LAKARA_LABEL[lakara].hi})`,
            en: `Build: "${english}"`
        },
        tiles: [...words, ...distractors],
        answer: words,
        orderFree: true,
        skill: 'sentence',
        item: `verb:${d.id}:${lakara}:${padasOf(d)[0]}:${p}:${v}`
    }
}

/** वाक्य निर्माता: build a sentence; checked by grammar, any valid order accepted. */
export const sentenceQuestion = (rand: Rand, level = 2, scope: TopicScope = {}): PracticeQuestion => {
    const anyScope = Boolean(scope.lakaras || scope.purushas || scope.vacanas)

    if (!anyScope && rand() < 0.2) {
        const pool = level <= 1 ? SENTENCES.slice(0, 5) : level === 2 ? SENTENCES.slice(0, 14) : SENTENCES
        const s = pick(pool, rand)

        return {
            kind: 'build',
            key: `sb:${s.words.join('-')}`,
            prompt: { hi: `बनाइए: "${s.meaning.hi}"`, en: `Build: "${s.meaning.en}"` },
            tiles: [...s.words, ...s.distractors],
            answer: s.words,
            orderFree: s.orderFree ?? true,
            skill: 'sentence'
        }
    }

    for (let attempt = 0; attempt < 8; attempt++) {
        const built = builtSentenceQuestion(rand, level, scope)
        if (built) return built
    }

    const s = SENTENCES[0]
    return { kind: 'build', key: `sb:${s.words.join('-')}`, prompt: { hi: `बनाइए: "${s.meaning.hi}"`, en: `Build: "${s.meaning.en}"` }, tiles: [...s.words, ...s.distractors], answer: s.words, orderFree: true, skill: 'sentence' }
}

/** कथा क्रम: put three to five lines of a story in order. */
export const storyOrderQuestion = (rand: Rand, level = 4): PracticeQuestion => {
    const stories = STORIES.filter(s => s.level <= level && s.sentences.length >= 3)
    const story = pick(stories.length > 0 ? stories : STORIES, rand)
    const length = Math.min(story.sentences.length, 3 + Math.floor(rand() * 3))
    const start = Math.floor(rand() * Math.max(1, story.sentences.length - length + 1))
    const items = story.sentences.slice(start, start + length).map(s => s.sa)

    return {
        kind: 'reorder',
        key: `so:${story.id}:${start}:${items.length}`,
        prompt: { hi: `"${story.title.hi}" के वाक्य सही क्रम में लगाइए`, en: `Put these lines from "${story.title.en}" in order` },
        items,
        skill: 'comprehension'
    }
}

// ── Games ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export type GameId =
    | 'dhatu-challenge'
    | 'lakara-pehchan'
    | 'shabdarupa-sadhana'
    | 'vibhakti-chayan'
    | 'sandhi-nirmata'
    | 'sandhi-viccheda'
    | 'samasa-pehchan'
    | 'vakya-nirmata'
    | 'shravan'
    | 'akshara-nirman'
    | 'dvivacana'
    | 'katha-krama'

export interface GameOptions {
    dhatuId?: string
    scope?: TopicScope
}

export interface GameDef {
    id: GameId
    sa: string
    title: Bi
    desc: Bi
    skill: SkillId
    icon: string
    length: number
    generate: (rand: Rand, level: number, opts?: GameOptions) => PracticeQuestion
}

const verbScope = (level: number, o?: GameOptions): VerbScope => ({
    level,
    dhatuIds: o?.dhatuId ? [o.dhatuId] : undefined,
    lakaras: o?.scope?.lakaras,
    vacanas: o?.scope?.vacanas,
    purushas: o?.scope?.purushas
})

export const GAMES: GameDef[] = [
    { id: 'dhatu-challenge', sa: 'धातु चुनौती', title: { hi: 'सही क्रिया-रूप चुनिए', en: 'Pick the right verb form' }, desc: { hi: 'कर्ता के लिए सही धातुरूप।', en: 'Match each subject with its verb form.' }, skill: 'verb-production', icon: 'zap', length: 10, generate: (r, l, o) => verbProductionQuestion(r, verbScope(l, o)) },
    {
        id: 'lakara-pehchan', sa: 'लकार पहचान', title: { hi: 'लकार पहचानिए', en: 'Spot the lakāra' }, desc: { hi: 'वर्तमान, भूत, भविष्य, आज्ञा या विधि?', en: 'Present, past, future, command or advice?' }, skill: 'lakara', icon: 'clock', length: 10,
        generate: (r, l, o) => {
            const scope = verbScope(l, o)
            const oneLakara = scope.lakaras?.length === 1

            return !oneLakara && r() < 0.75 ? lakaraIdentifyQuestion(r, { ...scope, level: l + 1 }) : verbIdentifyQuestion(r, scope)
        }
    },
    { id: 'shabdarupa-sadhana', sa: 'शब्दरूप साधना', title: { hi: 'शब्दरूप बनाइए', en: 'Build noun forms' }, desc: { hi: 'विभक्ति और वचन के अनुसार सही रूप।', en: 'The right form for each case and number.' }, skill: 'declension', icon: 'book-open', length: 10, generate: (r, l, o) => nounFormQuestion(r, { level: l, vibhaktis: o?.scope?.vibhaktis, vacanas: o?.scope?.vacanas }) },
    { id: 'vibhakti-chayan', sa: 'विभक्ति चयन', title: { hi: 'सही विभक्ति चुनिए', en: 'Choose the right case' }, desc: { hi: 'वाक्य के अर्थ से विभक्ति पहचानिए।', en: 'Let the meaning tell you the case.' }, skill: 'vibhakti', icon: 'compass', length: 10, generate: (r, l, o) => vibhaktiChoiceQuestion(r, o?.scope, l) },
    { id: 'sandhi-nirmata', sa: 'सन्धि निर्माता', title: { hi: 'शब्द जोड़िए', en: 'Join the words' }, desc: { hi: 'दो शब्द, एक सन्धि।', en: 'Two words, one sandhi.' }, skill: 'sandhi', icon: 'sparkles', length: 10, generate: (r, _l, o) => sandhiCombineQuestion(r, o?.scope) },
    { id: 'sandhi-viccheda', sa: 'सन्धि विच्छेद', title: { hi: 'सन्धि खोलिए', en: 'Undo the sandhi' }, desc: { hi: 'जुड़े शब्द को अलग कीजिए।', en: 'Split the joined word.' }, skill: 'sandhi', icon: 'route', length: 10, generate: (r, _l, o) => sandhiSplitQuestion(r, o?.scope) },
    { id: 'samasa-pehchan', sa: 'समास पहचान', title: { hi: 'समास पहचानिए', en: 'Identify the compound' }, desc: { hi: 'विग्रह से समास और समास से विग्रह।', en: 'From the analysis to the compound and back.' }, skill: 'sandhi', icon: 'layers', length: 10, generate: (r, _l, o) => samasaQuestion(r, o?.scope) },
    { id: 'vakya-nirmata', sa: 'वाक्य निर्माता', title: { hi: 'वाक्य बनाइए', en: 'Build sentences' }, desc: { hi: 'शब्द-टाइलों से पूरा वाक्य।', en: 'Whole sentences from word tiles.' }, skill: 'sentence', icon: 'square-pen', length: 10, generate: (r, l, o) => sentenceQuestion(r, l, o?.scope) },
    { id: 'shravan', sa: 'श्रवण अभ्यास', title: { hi: 'सुनकर पहचानिए', en: 'Listen and identify' }, desc: { hi: 'सुने गए शब्द या रूप को पहचानिए।', en: 'Recognise words and forms by ear.' }, skill: 'listening', icon: 'ear', length: 10, generate: (r, l, o) => listeningQuestion(r, l + 1, o?.scope) },
    { id: 'akshara-nirman', sa: 'अक्षर निर्माण', title: { hi: 'अक्षरों से शब्द', en: 'Build words from letters' }, desc: { hi: 'अक्षर जोड़कर शब्द और रूप बनाइए।', en: 'Assemble words and forms akshara by akshara.' }, skill: 'devanagari', icon: 'pen-line', length: 10, generate: (r, l, o) => aksharaQuestion(r, l + 1, o?.scope) },
    { id: 'dvivacana', sa: 'द्विवचन चुनौती', title: { hi: 'दो के लिए रूप', en: 'The dual challenge' }, desc: { hi: 'एकवचन से द्विवचन बनाइए।', en: 'Turn singular forms into duals.' }, skill: 'declension', icon: 'users', length: 10, generate: (r, l, o) => dualQuestion(r, o?.scope, l) },
    { id: 'katha-krama', sa: 'कथा क्रम', title: { hi: 'कहानी का क्रम', en: 'Story order' }, desc: { hi: 'कहानी के वाक्य सही क्रम में।', en: 'Put a story’s sentences in order.' }, skill: 'comprehension', icon: 'scroll-text', length: 5, generate: (r, l) => storyOrderQuestion(r, l + 2) }
]

export const getGame = (id: string): GameDef | undefined => GAMES.find(g => g.id === id)

/** Generate a set of questions, avoiding exact repeats within the set and any recently seen keys. */
export const generateSet = (gen: (rand: Rand) => PracticeQuestion, count: number, rand: Rand, avoid: Set<string> = new Set()): PracticeQuestion[] => {
    const out: PracticeQuestion[] = []
    const keys = new Set<string>()
    let attempts = 0

    while (out.length < count && attempts < count * 12) {
        attempts++
        const q = gen(rand)
        if (keys.has(q.key)) continue
        if (avoid.has(q.key) && attempts < count * 6) continue
        if (q.kind === 'mcq' && (q.options.length < 2 || q.answer < 0)) continue
        keys.add(q.key)
        out.push(q)
    }

    return out
}
